/**
 * Friendly connection-status messages (issue #29).
 *
 * Plaid reports problems as codes (ITEM_LOGIN_REQUIRED) and long developer
 * sentences ("the login details of this item have changed ..."). Neither
 * belongs in front of a person. This module is the ONE place a code becomes
 * words: a short status chip, a one-line message, and the action that fixes
 * it. The raw code and Plaid's sentence stay available for a "Details"
 * disclosure, for support.
 *
 * Kept as a plain catalog keyed by code so i18n (#30) can swap the strings for
 * translation keys without touching the page. The backend dashboard
 * (internal/core/services/dashboard_attention.go) follows the same semantics.
 */

export type ConnectionSeverity = 'success' | 'info' | 'warning' | 'error';

/** What fixes the problem. */
export type ConnectionAction =
  /** Plaid Link update mode, repair: sign in to the bank again. */
  | 'signIn'
  /** Plaid Link update mode requesting investments: widen consent. */
  | 'allowInvestments'
  | 'none';

export interface ConnectionMessage {
  /** Short status chip text, e.g. "Sign in again". */
  title: string;
  /** One-line explanation for the person. */
  message: string;
  action: ConnectionAction;
  severity: ConnectionSeverity;
}

const SIGN_IN_AGAIN: ConnectionMessage = {
  title: 'Sign in again',
  message: 'Sign-in expired — sign in again to keep syncing.',
  action: 'signIn',
  severity: 'warning',
};

const BANK_UNAVAILABLE: ConnectionMessage = {
  title: 'Bank unavailable',
  message: "Bank temporarily unavailable — we'll retry tonight.",
  action: 'none',
  severity: 'warning',
};

export const INVESTMENTS_NOT_SHARED: ConnectionMessage = {
  title: 'Investments not shared',
  message: 'Investments not shared — allow investments to see holdings, trades and dividends.',
  action: 'allowInvestments',
  severity: 'info',
};

/** The bank does not offer investments through Plaid: nothing to fix. */
export const PRODUCTS_NOT_SUPPORTED = 'PRODUCTS_NOT_SUPPORTED';

/** The catalog, keyed by Plaid error_code. */
export const CONNECTION_MESSAGES: Readonly<Record<string, ConnectionMessage>> = {
  ITEM_LOGIN_REQUIRED: SIGN_IN_AGAIN,
  INVALID_CREDENTIALS: {
    ...SIGN_IN_AGAIN,
    message: "The bank didn't accept the saved sign-in — sign in again to keep syncing.",
  },
  INVALID_MFA: {
    ...SIGN_IN_AGAIN,
    message: 'The bank needs a fresh verification code — sign in again to keep syncing.',
  },
  ITEM_LOCKED: {
    ...SIGN_IN_AGAIN,
    message: 'The bank locked this sign-in — unlock it with the bank, then sign in again.',
  },
  PENDING_EXPIRATION: {
    ...SIGN_IN_AGAIN,
    title: 'Renew access',
    message: 'Access to this bank is about to expire — sign in again to renew it.',
  },
  ADDITIONAL_CONSENT_REQUIRED: INVESTMENTS_NOT_SHARED,
  INSTITUTION_DOWN: BANK_UNAVAILABLE,
  INSTITUTION_NOT_RESPONDING: BANK_UNAVAILABLE,
  INSTITUTION_NOT_AVAILABLE: BANK_UNAVAILABLE,
  RATE_LIMIT_EXCEEDED: {
    title: 'Sync delayed',
    message: "The bank is busy — we'll retry shortly.",
    action: 'none',
    severity: 'info',
  },
};

/** Fallback for a code the catalog does not know. */
export const UNKNOWN_PROBLEM: ConnectionMessage = {
  title: 'Sync problem',
  message: "Something went wrong syncing this bank — we'll retry. See Details if it keeps happening.",
  action: 'none',
  severity: 'warning',
};

export const ACTIVE: ConnectionMessage = {
  title: 'Active', message: '', action: 'none', severity: 'success',
};

const DISCONNECTED: ConnectionMessage = {
  title: 'Disconnected', message: 'This bank is no longer connected.', action: 'none', severity: 'info',
};

/** The catalog entry for a code, or the generic fallback. */
export function messageForCode(code?: string | null): ConnectionMessage {
  const key = code?.trim().toUpperCase();
  return (key && CONNECTION_MESSAGES[key]) || UNKNOWN_PROBLEM;
}

/** Button label for an action, or null when there is nothing to press. */
export function actionLabel(action: ConnectionAction): string | null {
  switch (action) {
    case 'signIn': return 'Sign in again';
    case 'allowInvestments': return 'Allow investments';
    default: return null;
  }
}

export interface ConnectionFacts {
  status: string;
  last_error?: string | null;
  last_error_code?: string | null;
  investments_unavailable_code?: string | null;
  investments_unavailable_at?: string | null;
}

export interface ConnectionStatusView extends ConnectionMessage {
  /** Raw Plaid code, for the Details disclosure. */
  code?: string;
  /** Raw Plaid message, for the Details disclosure. */
  rawMessage?: string;
}

/**
 * How a connection's health is shown: worst problem first.
 *
 * 1. Needs re-auth -- always a sign-in, whatever the code says.
 * 2. Any other recorded error, through the catalog.
 * 3. Investments not shared -- unless the bank simply does not offer them
 *    (PRODUCTS_NOT_SUPPORTED), which needs nothing and shows nothing.
 * 4. Otherwise Active.
 */
export function connectionStatusView(c: ConnectionFacts): ConnectionStatusView {
  const code = c.last_error_code?.trim() || undefined;
  const rawMessage = c.last_error?.trim() || undefined;

  if (c.status === 'RequiresReauth') {
    const m = messageForCode(code);
    return { ...(m.action === 'signIn' ? m : SIGN_IN_AGAIN), code, rawMessage };
  }
  if (c.status === 'Disconnected') return { ...DISCONNECTED, code, rawMessage };
  if (code || rawMessage) return { ...messageForCode(code), code, rawMessage };

  const inv = c.investments_unavailable_code?.trim();
  if ((inv || c.investments_unavailable_at) && inv?.toUpperCase() !== PRODUCTS_NOT_SUPPORTED) {
    // Any other holdings failure is consent the Item lacks; reconnecting
    // with investments is the fix (the dashboard says the same).
    return { ...INVESTMENTS_NOT_SHARED, code: inv || undefined };
  }
  return { ...ACTIVE };
}

/** CSS colour for a severity, from the design tokens. */
export function severityColor(s: ConnectionSeverity): string {
  switch (s) {
    case 'success': return 'var(--inflow-text)';
    case 'info': return 'var(--info-text)';
    case 'error': return 'var(--danger-text)';
    default: return 'var(--warn-text)';
  }
}

/** What the owner has to do, in words, for a non-owner's message. */
function ownerTask(action: ConnectionAction): string | null {
  switch (action) {
    case 'signIn': return 'sign in again';
    case 'allowInvestments': return 'allow investments';
    default: return null;
  }
}

/**
 * The status line as the VIEWER should read it (issue #35). The fix needs the
 * owner's bank sign-in, so a household member who did not link the bank is
 * told who to ask rather than given an instruction they cannot carry out:
 * "Sign-in expired — ask Tia to sign in again."
 *
 * Args:
 *   v: The connection's status view.
 *   ownerName: The owner's display name when the viewer is NOT the owner;
 *     null or empty for the owner (or when nobody is named).
 */
export function viewerMessage(v: ConnectionMessage, ownerName?: string | null): string {
  const owner = ownerName?.trim();
  const task = ownerTask(v.action);
  if (!owner || !task) return v.message;
  const head = v.message.split(' — ')[0].replace(/[.\s]+$/, '');
  return `${head} — ask ${owner} to ${task}.`;
}

/** The status chip as the viewer should read it: "Waiting on Tia" when only
 * the owner can act. */
export function viewerTitle(v: ConnectionMessage, ownerName?: string | null): string {
  const owner = ownerName?.trim();
  return owner && ownerTask(v.action) ? `Waiting on ${owner}` : v.title;
}

/** "1 account" / "13 accounts"; null when the count is unknown or zero. */
export function accountCountLabel(n?: number | null): string | null {
  if (!n || n < 1) return null;
  return `${n} account${n === 1 ? '' : 's'}`;
}
